import type { BusinessHealth, HealthComponent } from './types';
import type { WindowAggregate } from './aggregateOrders';
import { pctChange } from './format';

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Map WoW/DoD GMV % into 0–100 demand score. */
export function demandScore(gmvPct: number | null): number | null {
  if (gmvPct == null) return null;
  // 100 if in [-5, +20]; linear toward 0 outside [-50, +100]
  if (gmvPct >= -5 && gmvPct <= 20) return 100;
  if (gmvPct < -5) {
    // -5 → 100, -50 → 0
    return clamp(Math.round(100 * (1 - (-5 - gmvPct) / 45)), 0, 100);
  }
  // >20: soft penalty to 70 at +100
  return clamp(Math.round(100 - ((gmvPct - 20) / 80) * 30), 0, 100);
}

export function fulfillmentScore(agg: WindowAggregate): number | null {
  if (agg.orderCount === 0) return null;
  const cancelRate = agg.canceled / agg.orderCount;
  const openRate = agg.unshipped / agg.orderCount;
  // Start 100; penalize
  let s = 100;
  s -= openRate * 80;
  s -= cancelRate * 120;
  return clamp(Math.round(s), 0, 100);
}

export function inventoryScore(zero: number | null, sample: number): number | null {
  if (zero == null || sample <= 0) return null;
  const rate = zero / sample;
  return clamp(Math.round(100 * (1 - rate)), 0, 100);
}

export function platformScore(opts: {
  connectionStatus: string;
  staleHours: number | null;
  ordersQuality?: string;
}): number | null {
  const st = opts.connectionStatus.toLowerCase();
  if (st === 'error' || st === 'disconnected' || st === 'failed') return 0;
  if (opts.ordersQuality && /fail|error|dead/i.test(opts.ordersQuality)) return 15;
  if (opts.staleHours == null) return 55;
  if (opts.staleHours > 48) return 20;
  if (opts.staleHours > 24) return 40;
  if (opts.staleHours > 6) return 70;
  return 95;
}

export function buildBusinessHealth(input: {
  yesterday: WindowAggregate;
  prior: WindowAggregate;
  inventoryZero: number | null;
  inventorySample: number;
  connectionStatus: string;
  staleHours: number | null;
  ordersQuality?: string;
  hrefs: { orders: string; inventory: string; connect: string };
}): BusinessHealth {
  const gmvPct = pctChange(input.yesterday.sales, input.prior.sales);
  const components: HealthComponent[] = [
    {
      id: 'C1',
      label: 'Demand',
      score: demandScore(gmvPct),
      status: demandScore(gmvPct) == null ? 'unavailable' : 'ready',
      reason: gmvPct == null ? 'Need OrderTotal on both days' : `DoD GMV ${gmvPct.toFixed(1)}%`,
      href: input.hrefs.orders,
    },
    {
      id: 'C2',
      label: 'Fulfillment',
      score: fulfillmentScore(input.yesterday),
      status: fulfillmentScore(input.yesterday) == null ? 'unavailable' : 'ready',
      reason:
        input.yesterday.orderCount === 0
          ? 'No yesterday orders'
          : `Open ${input.yesterday.unshipped}, canceled ${input.yesterday.canceled}`,
      href: input.hrefs.orders,
    },
    {
      id: 'C3',
      label: 'Inventory',
      score: inventoryScore(input.inventoryZero, input.inventorySample),
      status:
        inventoryScore(input.inventoryZero, input.inventorySample) == null
          ? 'unavailable'
          : 'ready',
      reason:
        input.inventorySample === 0
          ? 'Inventory sample empty'
          : `${input.inventoryZero ?? 0}/${input.inventorySample} zero in sample`,
      href: input.hrefs.inventory,
    },
    {
      id: 'C6',
      label: 'Platform trust',
      score: platformScore({
        connectionStatus: input.connectionStatus,
        staleHours: input.staleHours,
        ordersQuality: input.ordersQuality,
      }),
      status: 'ready',
      reason: `Connection ${input.connectionStatus}`,
      href: input.hrefs.connect,
    },
  ];

  // Cash + BB not wired without invent → count as unavailable total slots for confidence
  const optionalMissing = 2; // C4 cash, C5 BB gated / not scored this program
  const ready = components.filter((c) => c.status === 'ready' && c.score != null);
  const componentsTotal = components.length + optionalMissing;
  const componentsReady = ready.length;

  let overall: number | null = null;
  let band: BusinessHealth['band'] = 'unavailable';
  if (ready.length >= 3) {
    const sum = ready.reduce((a, c) => a + (c.score as number), 0);
    overall = Math.round(sum / ready.length);
    if (overall >= 80) band = 'safe';
    else if (overall >= 50) band = 'alert';
    else band = 'critical';
  }

  // Confidence: ready share + field coverage on order totals
  const fieldCov =
    input.yesterday.orderCount === 0
      ? 0.5
      : input.yesterday.ordersWithTotal / input.yesterday.orderCount;
  const readyShare = componentsReady / componentsTotal;
  const confidence = clamp(Math.round(100 * (0.55 * readyShare + 0.45 * fieldCov)), 0, 100);
  const confidenceLabel =
    confidence >= 70 ? 'high' : confidence >= 40 ? 'medium' : 'low';

  return {
    overall,
    band,
    componentsReady,
    componentsTotal,
    components,
    confidence,
    confidenceLabel,
  };
}
